import { FC, useRef } from "react";
import styled from "@emotion/styled";
import { Container as StyledContainer } from "./Layout/Container";
import { useApp } from "../AppProvider";
import { motion } from "framer-motion";
import { ChevronUpIcon } from "@heroicons/react/24/solid";
import instagram from "../assets/instagram.webp";
import vimeo from "../assets/vimeo.webp";
import youtube from "../assets/youtube.webp";
import logo from "../assets/logo_centre films_schwarz.webp";
import { logEvent } from "@firebase/analytics";
import { Link } from "react-router-dom";

const Container = styled(StyledContainer)(() => ({
  zIndex: 1,
  display: "flex",
  flexDirection: "column",
  height: "100%",
}));

const FooterWrapper = styled("footer")(() => ({
  width: "100%",
  position: "relative",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "center",
  padding: "2rem 0",
  color: "white",
  backgroundColor: "#fff",
}));

const NavLink = styled(Link)(() => ({
  backgroundColor: "transparent",
  border: "none",
  textDecoration: "none",
  color: "#333",
  fontSize: 14,
  fontWeight: 300,
  cursor: "pointer",
  padding: 0,
  "&:hover": {
    color: "#222",
  },
}));

const Divider = styled("div")(() => ({
  width: "100%",
  height: 0,
  borderTop: "1px solid rgba(0, 0, 0, 0.1)",
  margin: "auto 0 2rem 0",
}));

const Navigation = styled("nav")(() => ({
  color: "#333",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  "@media (min-width: 768px)": {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  gap: "2rem",
}));

const CentreFilms = styled("h5")(() => ({
  textAlign: "center",
  "@media (min-width: 768px)": {
    textAlign: "left",
  },
  color: "#222",
}));

const Links = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "0.5rem",
}));

const TopButton = styled(motion.button)(() => ({
  border: "none",
  background: "transparent",
  outline: "none",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  color: "#333",
  cursor: "pointer",
  gap: "0.5rem",
  "& span": {
    borderRadius: 999999,
    backgroundColor: "#333",
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    width: 30,
    height: 30,
    "& svg": {
      width: 16,
      height: 16,
      color: "#fff",
    },
  },
}));

const SocialLink = styled("a")(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.7rem",
  textDecoration: "none",
  color: "#333",
}));

const SocialImage = styled("div", {
  shouldForwardProp: (prop) => prop !== "src",
})<{
  src: string;
}>(({ src }) => ({
  backgroundImage: "url(" + src + ")",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  width: 30,
  height: 30,
}));

const Logo = styled("img")(() => ({
  width: 70,
  height: 70,
}));

const FirstRow = styled("div")(() => ({
  display: "flex",
  flexDirection: "column-reverse",
  gap: "4rem",
  "@media (min-width: 768px)": {
    flexDirection: "row",
    gap: 0,
  },
  justifyContent: "space-between",
}));

const LogoWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  "@media (min-width: 768px)": {
    alignItems: "flex-start",
  },
}));

export const Footer: FC = () => {
  const { analytics, setCookiesOpen } = useApp();
  const ref = useRef<HTMLDivElement | null>(null);

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    if (analytics) {
      logEvent(analytics, "back_to_top");
    }
  };

  const handleInstagram = () => {
    window.open("https://www.instagram.com/centrefilms", "_blank");
    if (analytics) {
      logEvent(analytics, "footer_instagram_click");
    }
  };

  const handleVimeo = () => {
    window.open("https://vimeo.com/martinfoerster", "_blank");
    if (analytics) {
      logEvent(analytics, "footer_vimeo_click");
    }
  };

  const handleYoutube = () => {
    if (analytics) {
      logEvent(analytics, "footer_youtube_click");
    }
  };

  const handleCookies = () => {
    setCookiesOpen(true);
    if (analytics) {
      logEvent(analytics, "cookie_settings");
    }
  };

  return (
    <FooterWrapper ref={ref}>
      <Divider />
      <Container>
        <FirstRow>
          <LogoWrapper>
            <Logo loading={"lazy"} src={logo} alt="logo" />
            <CentreFilms>Centre Films GmbH & Co. KG</CentreFilms>
          </LogoWrapper>
          <TopButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type={"button"}
            onClick={handleBackToTop}
          >
            <span>
              <ChevronUpIcon />
            </span>
            Zurück nach oben
          </TopButton>
        </FirstRow>
        <Navigation>
          <Links>
            <NavLink to={"/impressum"} type={"button"}>
              Impressum
            </NavLink>
            <span>•</span>
            <NavLink type={"button"} to={"/datenschutz"}>
              Datenschutzerklärung
            </NavLink>
            <span>•</span>
            <NavLink
              to={"/"}
              type={"button"}
              onClick={(e) => {
                e.preventDefault();
                handleCookies();
              }}
            >
              Cookie Einstellungen
            </NavLink>
          </Links>
          <div style={{ flexGrow: 1 }} />
          <SocialLink
            href={"https://www.instagram.com/centrefilms"}
            target={"_blank"}
            rel="noreferrer"
            onClick={handleInstagram}
          >
            <SocialImage src={instagram} />
            <span>Instagram</span>
          </SocialLink>
          <SocialLink
            href={"https://vimeo.com/martinfoerster"}
            target={"_blank"}
            rel="noreferrer"
            onClick={handleVimeo}
          >
            <SocialImage src={vimeo} />
            <span>Vimeo</span>
          </SocialLink>
          <SocialLink
            href={"https://www.youtube.com/user/artefect"}
            target={"_blank"}
            rel="noreferrer"
            onClick={handleYoutube}
          >
            <SocialImage src={youtube} />
            <span>Youtube</span>
          </SocialLink>
        </Navigation>
      </Container>
    </FooterWrapper>
  );
};
